import { get } from 'lodash-es'

type JsonObject = Record<string, any>
type UserCenterRedirectMap = Record<string, string>

export const DEFAULT_USER_CENTER_BASE_PATH = '/user-center'
export const MALL_USER_CENTER_HOME_PATH = '/mall/home'
export const COLLECTION_USER_CENTER_HOME_PATH = '/collection/home'

export function parseJsonObject(rawValue: unknown): JsonObject {
  if (!rawValue) {
    return {}
  }

  if (typeof rawValue === 'object') {
    return Array.isArray(rawValue) ? {} : rawValue as JsonObject
  }

  if (typeof rawValue !== 'string') {
    return {}
  }

  try {
    const parsedValue = JSON.parse(rawValue)
    return parsedValue && typeof parsedValue === 'object' && !Array.isArray(parsedValue)
      ? parsedValue
      : {}
  }
  catch {
    return {}
  }
}

export function parseJsonArray(rawValue: unknown): unknown[] {
  if (!rawValue) {
    return []
  }

  if (Array.isArray(rawValue)) {
    return rawValue
  }

  if (typeof rawValue !== 'string') {
    return []
  }

  try {
    const parsedValue = JSON.parse(rawValue)
    return Array.isArray(parsedValue) ? parsedValue : []
  }
  catch {
    return []
  }
}

function normalizeOtherJsonCandidates(candidates: unknown[]): JsonObject[] {
  return candidates
    .map(source => parseJsonObject(source))
    .filter(source => Object.keys(source).length > 0)
}

export function getCollectionUserCenterOtherJsonList(storeInfo: unknown): JsonObject[] {
  const storeInfoObject = parseJsonObject(storeInfo)
  const offiaccountFollowData = parseJsonObject(get(storeInfoObject, 'offiaccountFollowData'))
  const offiaccountFollowUrlData = parseJsonObject(get(storeInfoObject, 'offiaccountFollowUrlData'))
  const offiaccountFollowUrl = parseJsonObject(get(storeInfoObject, 'offiaccountFollowUrl'))
  const storePageInfo = parseJsonObject(get(storeInfoObject, 'storePageInfo'))

  return normalizeOtherJsonCandidates([
    get(storeInfoObject, 'offiaccountFollowData.otherJson'),
    get(offiaccountFollowData, 'otherJson'),
    get(storeInfoObject, 'offiaccountFollowUrlData.otherJson'),
    get(offiaccountFollowUrlData, 'otherJson'),
    get(offiaccountFollowUrl, 'otherJson'),
    get(storeInfoObject, 'otherJson'),
    get(storeInfoObject, 'storePageInfo.otherJson'),
    get(storePageInfo, 'otherJson'),
    storePageInfo,
    storeInfoObject,
  ])
}

export function isCollectionUserCenterStoreInfo(storeInfo: unknown): boolean {
  return getCollectionUserCenterOtherJsonList(storeInfo).some((otherJson) => {
    return get(otherJson, 'mallSiteConfig.isCollectionUserCenter') === true
  })
}

export function getUserCenterSiderMenusConfigStoreInfo(storeInfo: unknown): unknown[] {
  for (const otherJson of getCollectionUserCenterOtherJsonList(storeInfo)) {
    const config = parseJsonArray(get(otherJson, 'mallSiteConfig.userCenterSiderMenusConfig'))
    if (config.length) {
      return config
    }
  }

  return []
}

function normalizeRedirectMap(rawMap: unknown): UserCenterRedirectMap {
  const map = parseJsonObject(rawMap)

  return Object.entries(map).reduce<UserCenterRedirectMap>((result, [sourcePath, targetPath]) => {
    const source = String(sourcePath || '').trim()
    const target = typeof targetPath === 'string' || typeof targetPath === 'number'
      ? String(targetPath).trim()
      : ''

    if (source && target) {
      result[source] = target
    }

    return result
  }, {})
}

export function getUserCenterRedirectMapStoreInfo(storeInfo: unknown): UserCenterRedirectMap {
  for (const otherJson of getCollectionUserCenterOtherJsonList(storeInfo)) {
    const redirectMap = normalizeRedirectMap(get(otherJson, 'mallSiteConfig.userCenterRedirectMap'))
    if (Object.keys(redirectMap).length) {
      return redirectMap
    }
  }

  return {}
}

export function isCollectionUserCenterStoreInfoList(storeInfoList: unknown): boolean {
  const list = Array.isArray(storeInfoList) ? storeInfoList : [storeInfoList]
  return list.some(storeInfo => isCollectionUserCenterStoreInfo(storeInfo))
}

export function getUserCenterRedirectMapStoreInfoList(storeInfoList: unknown): UserCenterRedirectMap {
  const list = Array.isArray(storeInfoList) ? storeInfoList : [storeInfoList]

  for (const storeInfo of list) {
    const redirectMap = getUserCenterRedirectMapStoreInfo(storeInfo)
    if (Object.keys(redirectMap).length) {
      return redirectMap
    }
  }

  return {}
}

export function getStoreInfoDataMapValues(storeInfoDataMap: unknown): unknown[] {
  if (!storeInfoDataMap || typeof storeInfoDataMap !== 'object' || Array.isArray(storeInfoDataMap)) {
    return []
  }

  return Object.values(storeInfoDataMap)
}

export function getStoredStoreInformation(): JsonObject {
  if (typeof window === 'undefined') {
    return {}
  }

  return parseJsonObject(window.localStorage.getItem('StoreInformation'))
}

function splitPathAndSuffix(path = '') {
  const stringPath = String(path || '')
  const queryIndex = stringPath.indexOf('?')
  const hashIndex = stringPath.indexOf('#')
  const suffixStartIndex = [queryIndex, hashIndex]
    .filter(index => index >= 0)
    .sort((lastIndex, nextIndex) => lastIndex - nextIndex)[0]

  return {
    pathname: suffixStartIndex === undefined ? stringPath || '/' : stringPath.slice(0, suffixStartIndex) || '/',
    suffix: suffixStartIndex === undefined ? '' : stringPath.slice(suffixStartIndex),
  }
}

function normalizePathname(pathname = '') {
  const normalizedPath = pathname.startsWith('/') ? pathname : `/${pathname}`

  if (normalizedPath.length <= 1) {
    return '/'
  }

  return normalizedPath.replace(/\/+$/, '')
}

function normalizeLocalAbsolutePath(path = '') {
  if (!/^(?:https?:)?\/\//.test(path)) {
    return path
  }

  try {
    const base = typeof window !== 'undefined' ? window.location.origin : 'http://localhost'
    const url = new URL(path, base)
    const currentHost = typeof window !== 'undefined' ? window.location.host : ''
    const isCurrentHost = currentHost && url.host === currentHost
    const isLocalDevHost = ['localhost', '127.0.0.1', '0.0.0.0'].includes(url.hostname)

    if (isCurrentHost || isLocalDevHost) {
      return `${url.pathname}${url.search}${url.hash}`
    }
  }
  catch {
    return path
  }

  return path
}

function normalizeUserCenterRedirectSourcePath(path = '', userCenterBasePath = DEFAULT_USER_CENTER_BASE_PATH) {
  const normalizedLocalPath = normalizeLocalAbsolutePath(String(path || '').trim())
  if (!normalizedLocalPath || /^(?:https?:)?\/\//.test(normalizedLocalPath)) {
    return ''
  }

  const { pathname, suffix } = splitPathAndSuffix(normalizedLocalPath)
  const basePath = normalizePathname(userCenterBasePath)
  let normalizedPathname = normalizePathname(pathname)

  if (normalizedPathname === basePath) {
    normalizedPathname = '/'
  }
  else if (normalizedPathname.startsWith(`${basePath}/`)) {
    normalizedPathname = normalizePathname(normalizedPathname.slice(basePath.length))
  }

  return `${normalizedPathname}${suffix}`
}

function normalizeUserCenterRedirectTargetPath(path = '', userCenterBasePath = DEFAULT_USER_CENTER_BASE_PATH) {
  const normalizedLocalPath = normalizeLocalAbsolutePath(String(path || '').trim())
  if (!normalizedLocalPath) {
    return ''
  }

  if (/^(?:https?:)?\/\//.test(normalizedLocalPath)) {
    return normalizedLocalPath
  }

  const { pathname, suffix } = splitPathAndSuffix(normalizedLocalPath)
  const basePath = normalizePathname(userCenterBasePath)
  const normalizedPathname = normalizePathname(pathname)

  if (normalizedPathname === basePath || normalizedPathname.startsWith(`${basePath}/`)) {
    return `${normalizedPathname}${suffix}`
  }

  return `${basePath}${normalizedPathname === '/' ? '' : normalizedPathname}${suffix}`
}

function hasPathSuffix(path = '') {
  return Boolean(splitPathAndSuffix(normalizeLocalAbsolutePath(String(path || '').trim())).suffix)
}

export function isExternalRedirectPath(path = ''): boolean {
  return /^(?:https?:)?\/\//.test(String(path || '').trim())
}

export function resolveUserCenterRedirectPath(
  redirectMap: UserCenterRedirectMap,
  currentFullPath = '',
  userCenterBasePath = DEFAULT_USER_CENTER_BASE_PATH,
): string {
  const entries = Object.entries(redirectMap || {})
  if (!entries.length || !currentFullPath) {
    return ''
  }

  const currentSourcePath = normalizeUserCenterRedirectSourcePath(currentFullPath, userCenterBasePath)
  if (!currentSourcePath) {
    return ''
  }

  const { pathname: currentPathname, suffix: currentSuffix } = splitPathAndSuffix(currentSourcePath)
  const currentPathOnly = normalizePathname(currentPathname)
  let matchedTarget = ''
  let shouldPreserveSuffix = false

  for (const [sourcePath, targetPath] of entries) {
    const normalizedSourcePath = normalizeUserCenterRedirectSourcePath(sourcePath, userCenterBasePath)
    if (normalizedSourcePath === currentSourcePath) {
      matchedTarget = targetPath
      shouldPreserveSuffix = !hasPathSuffix(targetPath)
      break
    }
  }

  if (!matchedTarget) {
    for (const [sourcePath, targetPath] of entries) {
      const normalizedSourcePath = normalizeUserCenterRedirectSourcePath(sourcePath, userCenterBasePath)
      const { pathname, suffix } = splitPathAndSuffix(normalizedSourcePath)

      if (!suffix && normalizePathname(pathname) === currentPathOnly) {
        matchedTarget = targetPath
        shouldPreserveSuffix = Boolean(currentSuffix) && !hasPathSuffix(targetPath)
        break
      }
    }
  }

  if (!matchedTarget) {
    return ''
  }

  const targetPath = normalizeUserCenterRedirectTargetPath(matchedTarget, userCenterBasePath)
  const nextPath = shouldPreserveSuffix ? `${targetPath}${currentSuffix}` : targetPath
  if (isExternalRedirectPath(nextPath)) {
    return nextPath
  }

  const nextSourcePath = normalizeUserCenterRedirectSourcePath(nextPath, userCenterBasePath)

  return nextSourcePath && nextSourcePath !== currentSourcePath ? nextPath : ''
}
